import React from 'react';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router';

import useLoadingStore from 'zustands/useLoadingStore';
import useApi from 'hooks/useApi';
import useErrorLog from 'hooks/useErrorLog';

import { phoneNumberAddHypen } from 'constants/utils';
import { regExp_phone } from 'constants/regExp';
import { IInviteItem } from 'types/employee-registration';

import Button from 'common/button/button';
import TextInput from 'common/text-input/text-input';
import PlainNavbar from 'components/plainNavbar/plainNavbar';
import './register-card.scss';
import useAlertConfirmStore from 'zustands/useAlertConfirmStore';

const RegisterCard: React.FC = () => {
  const navigate = useNavigate();

  const { setLoadingState } = useLoadingStore();
  const { setModalState } = useAlertConfirmStore();

  const { webApiNode } = useApi();
  const { checkErrorLog } = useErrorLog();

  const [error, setError] = React.useState<{ name: boolean; phone: boolean }>({
    name: false,
    phone: false,
  });
  const [inviteForm, setInviteForm] = React.useState<IInviteItem>({
    name: '',
    phone: '',
  });

  const handleInviteForm = (event: any) => {
    const { name, value } = event.target;
    setInviteForm((values: any) => ({ ...values, [name]: name === 'phone' ? phoneNumberAddHypen(value) : value }));
  };

  const validCheck = () => {
    let valid = true;
    if (inviteForm.name === '') {
      valid = false;
      setError((values: any) => ({ ...values, name: true }));
    } else {
      setError((values: any) => ({ ...values, name: false }));
    }
    if (inviteForm.phone.length !== 13) {
      valid = false;
      setError((values: any) => ({ ...values, phone: true }));
    } else {
      if (!regExp_phone.test(inviteForm.phone)) {
        valid = false;
        setError((values: any) => ({ ...values, phone: true }));
      } else {
        setError((values: any) => ({ ...values, phone: false }));
      }
    }
    return valid;
  };

  const sendInvite = async () => {
    if (validCheck()) {
      try {
        setLoadingState({ isFetchLoading: true });
        const { data }: AxiosResponse['data'] = await webApiNode('post', `/auth/inviteEmp`, {
          LIST: [{ NAME: inviteForm.name, PHONE: inviteForm.phone.split('-').join('') }],
        });

        if (data.resultCode === 0) {
          setModalState({
            isModalShow: true,
            title: '직원초대 완료',
            desc: '초대확인 알림이 오면 [승인대기중] 목록에서 합류를 승인해 주세요. (초대받은 직원이 앱에 로그인 하게 되면 초대확인 알림이 도착합니다)',
          });
          navigate(-1);
        } else {
          throw new Error(data.resultMsg);
        }
      } catch (err) {
        checkErrorLog(err, ' /auth/inviteEmp');
      } finally {
        setLoadingState({ isFetchLoading: false });
      }
    }
  };

  return (
    <div className="register-card__background">
      <PlainNavbar
        route="employee-registration"
        customButton={
          <div>
            <Button label="초대문자 보내기" type="type2" height={42} action={sendInvite} />
          </div>
        }
        backgroundColor="#f5f5f5">
        <div className="register-card">
          <div className="register-card__head">
            <div className="register-card__head__head-text">직원초대</div>
            <div className="register-card__head__subhead-text">초대할 직원의 정보를 입력해주세요.</div>
          </div>
          <div className="register-card__body">
            <div className="register-card__card-profile-changes">
              <table className="register-card__body__table">
                <tr>
                  <td className="register-card__body__left">
                    <div className="basic-info">
                      <div className="basic-info__head">기본정보</div>
                      <div className="basic-info__form">
                        <div className="basic-info__text-inputs">
                          <TextInput
                            value={inviteForm.name}
                            inputData={handleInviteForm}
                            label="이름*"
                            placeholder="이름 입력"
                            inputName="name"
                            maxLength={6}
                            errorMessage={error.name ? '이름을 입력해 주세요' : ''}
                          />
                        </div>
                        <div className="basic-info__text-inputs">
                          <TextInput
                            value={inviteForm.phone}
                            inputData={handleInviteForm}
                            label="휴대폰번호*"
                            placeholder="휴대폰번호 입력"
                            inputName="phone"
                            maxLength={13}
                            errorMessage={error.phone ? '올바른 휴대폰번호를 입력해 주세요' : ''}
                          />
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="register-card__body__right">
                    <div className="info__part1">
                      <div className="info__head">초대문자가 안온다면?</div>
                      <div className="info__body">
                        웹발신 또는 스팸차단서비스를 이용할 경우, 카카오톡 링크를 받지 못 할 수 있습니다.
                      </div>
                    </div>
                  </td>
                </tr>
              </table>
            </div>
          </div>
        </div>
      </PlainNavbar>
    </div>
  );
};
export default RegisterCard;
